import { useEditor } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { BaseReactPlayerProps } from "react-player/base";
import ReactPlayer from "react-player/lazy";
import styled, { css } from "styled-components";

import { trackLibraryVideoShareClick } from "@/content-hub/analytics/video-library-events";
import { PlayerOverlay } from "@/content-hub/components/video-player";
import {
  getOrganizationExternalId,
  getPageSlug,
  getPageUrl,
} from "@/content-hub/store/selectors";
import { copyContentHubInPlayerVideoShareDeepLink } from "@/content-hub/utils/copy-content-hub-browser-video-share";
import { useAppSelector } from "@/redux/store";
import togglePageScrollLock from "@/utils/toggle-page-scroll-lock";

import PlayIcon from "./assets/playIcon";
import {
  isContentHubInPlayerSharePath,
  isPublishedContentHubVideoSharePath,
} from "./is-preview-page";
import { resolveSectionIdFromElement } from "./resolve-section-id";
import { Caption, Thumbnail } from "./styles";
import { useAutoPlayFromUrl } from "./use-auto-play-from-url";
import { usePlayerToggle } from "./use-player-toggle";
import { writeVideoShareUrl } from "./write-video-share-url";

type EmbedVideoCardProps = {
  url: string;
  playerOptions?: BaseReactPlayerProps;
  aspectRatio?: "square" | "wide";
  title?: string;
  thumbnail?: string;
  showCaption?: boolean;
  videoId?: string;
  /** Craft node id for single-video deep links; must match settings share URL `sectionId`. */
  shareSectionId?: string | null;
  /**
   * When true, disables query-param autoplay, in-player share, and address-bar sync
   * for this card regardless of `contentHubDirectVideoSharing` (single-video legacy).
   */
  suppressDirectVideoSharingFeatures?: boolean;
  /** Rich-text description below the player in preview / published views. */
  overlayDescriptionHtml?: string;
};

export const EmbedVideoCard = (props: EmbedVideoCardProps) => {
  const {
    url,
    playerOptions = {},
    aspectRatio = "wide",
    title,
    thumbnail,
    showCaption = false,
    videoId,
    shareSectionId,
    suppressDirectVideoSharingFeatures = false,
    overlayDescriptionHtml,
  } = props;

  const containerRef = useRef<HTMLButtonElement | null>(null);
  const resolvedVideoId = videoId ?? url;
  const resolvedVideoName = title || "";

  const { isEditorEnabled } = useEditor((state) => ({
    isEditorEnabled: state.isEnabled(),
  }));

  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const pageSlug = useAppSelector(getPageSlug);
  const pageUrl = useAppSelector(getPageUrl);

  const flags = useFlags();
  const directVideoSharingEnabled =
    !suppressDirectVideoSharingFeatures &&
    Boolean(flags.contentHubDirectVideoSharing);

  const { isPlayerOpen, openPlayer, closePlayer } = usePlayerToggle({
    isEditorEnabled,
    hasPlayableUrl: !!url,
  });

  useEffect(
    function preventScrollOnPlay() {
      togglePageScrollLock({
        method: isPlayerOpen ? "add" : "remove",
        withOffset: false,
      });
    },
    [isPlayerOpen]
  );

  useAutoPlayFromUrl({
    videoId: resolvedVideoId,
    legacyEmbedUrl:
      typeof url === "string" && /^https?:\/\//i.test(url) ? url : undefined,
    isEditorEnabled,
    containerRef,
    openPlayer,
    directVideoSharingEnabled,
    shareSectionId,
    analytics: { kind: "embed", id: resolvedVideoId },
  });

  const canUseInPlayerShare =
    typeof window !== "undefined" &&
    directVideoSharingEnabled &&
    isContentHubInPlayerSharePath(window.location.pathname);

  const syncAddressBarQueryOnPlay =
    typeof window !== "undefined" &&
    directVideoSharingEnabled &&
    !isEditorEnabled &&
    isPublishedContentHubVideoSharePath(window.location.pathname);

  const handleShare = async () => {
    if (!canUseInPlayerShare || typeof window === "undefined") return;

    trackLibraryVideoShareClick({ kind: "embed", id: resolvedVideoId });
    await copyContentHubInPlayerVideoShareDeepLink({
      organizationExternalId,
      pageSlug,
      pageUrl,
      getVideoCardElement: () => containerRef.current,
      videoId: resolvedVideoId,
      videoName: resolvedVideoName || null,
    });
  };

  const handlePlay = () => {
    if (syncAddressBarQueryOnPlay) {
      const sectionId =
        shareSectionId?.trim() ||
        resolveSectionIdFromElement(containerRef.current);

      if (resolvedVideoId && sectionId) {
        writeVideoShareUrl({
          videoId: resolvedVideoId,
          sectionId,
          videoName: resolvedVideoName,
          locationTag: "content-hub/embed-video-card:url-update",
        });
      }
    }

    openPlayer();
  };

  return (
    <>
      <RelativeContainer
        ref={containerRef}
        $enabled={!isEditorEnabled}
        onClick={handlePlay}
        type="button"
        aria-label={title ? `Play ${title}` : "Play video"}
        data-testid="embed-video-ewcard"
      >
        {thumbnail ? (
          <Thumbnail
            $aspectRatio={aspectRatio}
            alt={title || ""}
            src={thumbnail}
          />
        ) : (
          <ReactPlayer
            url={url}
            controls
            width="100%"
            height="100%"
            {...playerOptions}
            style={{ pointerEvents: "none" }}
          />
        )}
        {showCaption && <Caption>{title || ""}</Caption>}
        <PlayIconContainer>
          {thumbnail && (
            <>
              <span className="sr-only">Play video</span>
              {!isPlayerOpen && <PlayIcon />}
            </>
          )}
        </PlayIconContainer>
      </RelativeContainer>
      {isPlayerOpen &&
        createPortal(
          <PlayerOverlay
            title={title}
            onClose={closePlayer}
            onShareClick={canUseInPlayerShare ? handleShare : undefined}
            descriptionHtml={overlayDescriptionHtml}
          >
            <ReactPlayer
              url={url}
              controls
              playing
              width="100%"
              height="100%"
              {...playerOptions}
              style={{
                pointerEvents: isEditorEnabled ? "none" : "initial",
              }}
            />
          </PlayerOverlay>,
          document.body
        )}
    </>
  );
};

const RelativeContainer = styled.button<{ $enabled: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  display: block;
  cursor: ${({ $enabled }) => ($enabled ? "pointer" : "default")};

  ${({ $enabled }) => {
    return css`
      &:hover {
        cursor: ${$enabled ? "pointer" : "inherit"};
      }
    `;
  }}

  &:disabled {
    cursor: default;
  }
`;

const PlayIconContainer = styled.div`
  --icon-size: 60px;
  position: absolute;
  top: calc(50% - (var(--icon-size) / 2));
  left: calc(50% - (var(--icon-size) / 2));
  width: var(--icon-size);
  height: var(--icon-size);

  cursor: inherit;
  pointer-events: none;

  & > svg {
    width: var(--icon-size);
    height: var(--icon-size);
  }
`;
